import * as React from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router'
import { LogOut, Menu, type LucideIcon } from 'lucide-react'
import { BrandMark } from '@/components/BrandMark'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

export interface AppNavItem {
  to: string
  label: string
  icon: LucideIcon
}

interface AppShellProps {
  homeTo: string
  brandSuffix?: string
  nav: AppNavItem[]
  /** Above the nav (the organization switcher on the dashboard). */
  header?: React.ReactNode
  userEmail?: string
  onSignOut: () => void
  /** Replaces the page outlet (e.g. "you don't belong to an organization yet"). */
  children?: React.ReactNode
  /** Changing this remounts the page (the dashboard keys it by organization). */
  outletKey?: string
  /** Above every page (the dashboard's test-mode banner). */
  banner?: React.ReactNode
}

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'flex h-8 items-center gap-2 rounded-md px-2 text-body font-medium transition-colors [&_svg]:size-4',
    isActive
      ? 'bg-selected text-link'
      : 'text-muted-foreground hover:bg-accent hover:text-foreground',
  )
}

function SidebarBody({ homeTo, brandSuffix, nav, header, userEmail, onSignOut }: AppShellProps) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-12 items-center px-4">
        <Link to={homeTo} className="rounded-md focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none">
          <BrandMark {...(brandSuffix ? { suffix: brandSuffix } : {})} />
        </Link>
      </div>
      {header ? <div className="px-3 pb-3">{header}</div> : null}
      <nav aria-label="Main" className="flex flex-1 flex-col gap-0.5 px-3">
        {nav.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} className={navLinkClass}>
            <Icon />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="flex flex-col gap-3 border-t border-border p-3">
        {userEmail ? <p className="truncate px-1 text-caption text-muted-foreground">{userEmail}</p> : null}
        <div className="flex items-center justify-between gap-2">
          <ThemeToggle />
          <Tooltip content="Sign out">
            <Button variant="ghost" size="icon-sm" aria-label="Sign out" onClick={onSignOut}>
              <LogOut />
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}

/**
 * The dashboard and ops shell: compact density, a fixed left sidebar on wide
 * screens, and a slide-in menu on narrow ones. Pages render in the outlet.
 */
export function AppShell(props: AppShellProps) {
  const location = useLocation()
  // The page the mobile menu was opened on: navigating anywhere else closes it.
  const [menuOpenOn, setMenuOpenOn] = React.useState<string | null>(null)
  const menuOpen = menuOpenOn === location.pathname

  function setMenuOpen(open: boolean): void {
    setMenuOpenOn(open ? location.pathname : null)
  }

  return (
    <div data-density="compact" className="min-h-svh bg-canvas text-body text-foreground">
      <aside className="fixed inset-y-0 left-0 hidden w-60 border-r border-border bg-card lg:block">
        <SidebarBody {...props} />
      </aside>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" closeLabel="Close menu" data-density="compact" aria-describedby={undefined}>
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <SidebarBody {...props} />
        </SheetContent>
      </Sheet>

      <div className="lg:pl-60">
        <header className="sticky top-0 z-30 flex h-12 items-center gap-2 border-b border-border bg-card px-3 lg:hidden">
          <Button variant="ghost" size="icon" aria-label="Open menu" onClick={() => setMenuOpen(true)}>
            <Menu />
          </Button>
          <BrandMark {...(props.brandSuffix ? { suffix: props.brandSuffix } : {})} />
        </header>
        <main className="mx-auto w-full max-w-6xl px-4 py-6 lg:px-8 lg:py-8">
          {props.banner}
          {props.children ?? <Outlet key={props.outletKey} />}
        </main>
      </div>
    </div>
  )
}
